// client-question-1 — "Reply Bubble" (organic, v1 port)
//
// The TikTok reply-sticker pattern: a white speech bubble carrying a (fake-
// anonymised) client question pops in top-left at frame 0 and then NEVER moves
// — its stillness against the changing b-roll is the signature look. Answer
// beats swap in the lower third, one per clip, with an optional CTA beat last.
//
// Renderdoc port of the v1 ClientQuestionLayer
// (packages/remotion/src/components/client-question-layer.tsx), built entirely
// from plain generic blocks:
//   - spine: procedure b-roll, full-bleed, looping, beat-synced cuts every 4
//     beats (template def `beatsPerEdit: 4`).
//   - spine: a `solid` carrying v1's 4-stop vertical scrim gradient verbatim
//     (the `solid` colour field accepts any CSS background string).
//   - overlay: a `group` — ONE white speech-bubble card (a vertical `stack`
//     carrying the bubble Frame: background, radius 22, padding, shadow and the
//     bottom-left tail) wrapping two `text` children, the grey "Replying to …"
//     label above the near-black question. Pinned top-left at v1's bubble
//     origin and bubble width.
//   - overlay: a sequential `staggered-list` in the lower third — one answer
//     beat on screen at a time, plus the optional CTA as the trail.
//
// v1 design source of truth (ClientQuestionLayer):
//   - scrim: linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.14) 34%,
//     rgba(0,0,0,0.2) 62%, rgba(0,0,0,0.58) 100%)
//   - bubble: top 8.5%, left 6%, width 62%, rgba(255,255,255,0.97), radius 22,
//     spring pop over 10 frames (damping 14 / stiffness 160 / mass 0.7)
//   - "Replying to {asker}": 26px / 600 / rgba(21,22,28,0.55)
//   - question: 40px / 700 / slate, lineHeight 1.28
//   - answer beat: 50px / 800 / white on rgba(13,15,24,0.6) radius 16, enters
//     over 8 frames (fade + 18px rise), fades out over the last 5 frames
//   - CTA beat: 40px / 800 / white on a solid slate pill (radius 999)
//
// CLOSED by the `group` block (was: two disconnected white pills, centred text,
// no tail — the reported defect):
//   - The bubble is now ONE card: a `stack` whose `container` Frame carries
//     v1's rgba(255,255,255,0.97) background, radius 22, 22×28 padding,
//     '0 10px 34px rgba(0,0,0,0.28)' shadow and `tail: 'bottom-left'` — the
//     tail the format is named for.
//   - Both lines are `text` NODES inside that card, so they keep their own
//     colours: the label's 55%-opacity grey and the question's near-black
//     slate, instead of `pill`'s forced #111.
//   - `align: 'start'` + `align: 'left'` make both lines flush-left inside the
//     card (v1), instead of centred.
//   - The card pops as ONE unit over exactly v1's 10 frames
//     (`entranceDurationFrames: 10` on the stack), not per-line over the
//     staggered-list default of 12.
//   - v1's question line-height 1.28, now that `TypeStyleOverride.lineHeight`
//     exists (absent → the renderer's 1.25 default, which is exactly v1's
//     answer-beat value, so only the question needed stating).
//   - THE COPY IS NOW A CLIENT'S QUESTION, NOT A MARKETING LINE, and it says so
//     IN THIS FILE. The bubble is a QUOTE; `role: 'hook'` was a slot, not a
//     voice, so the contract had to live as a `TEMPLATE_ROLE_GUIDANCE` entry in
//     packages/features. It is now the `question` field's own `description` in
//     the `content` block below, beside the slot that consumes it.
//   - THE ASKER. This template declares `asker`, so the reply label finally
//     reads "Replying to alex_r" — v1's `clientQuestion.asker`, recovered.
//
// FIDELITY GAPS vs v1 that REMAIN (v2 has no exit animations and no easing or
// spring configuration):
//   - The bubble's spring pop is approximated with the `pop` animation token;
//     v2 owns the spring constants (v1: damping 14 / stiffness 160 / mass 0.7)
//     and the transform origin (v1 scales from `top left`).
//   - The answer beats below stay a `staggered-list`. `group`'s `repeat` can
//     reveal N items sequentially, but it cannot append ONE final, differently
//     styled element (v1's optional CTA pill) to that same sequential
//     timeline — a `stack` gives its children no per-child entrance/exit
//     window, and only `repeat` assigns one. `staggered-list.trail` still does
//     that in one line, so the lower third is left alone.
//   - Answer beats hard-cut to the next beat instead of fading out over 5
//     frames (v2 sequential reveal has no exit), and their entrance runs on the
//     staggered-list default (12 frames) rather than v1's 8 — `ListElement`
//     carries no per-element entrance duration.
//   - The dark beat plate is `container: 'plate'` (rgba(0,0,0,0.7), radius 16)
//     vs v1's rgba(13,15,24,0.6) — same radius, slightly flatter black; the CTA
//     beat is `container: 'button'` (#111, radius 14) vs v1's fully-round slate
//     pill.
const FPS = 30;
// Fallback length only (music-only template, no narration to drive it): ~3
// answer beats + a CTA at one 4-beat clip each.
const TOTAL_FRAMES = 12 * FPS;
// v1's "Replying to …" label: 26px / 600 / rgba(21,22,28,0.55). Shared by the
// literal half and the asker half so the one line cannot render in two looks.
const REPLY_LABEL_STYLE = {
    fontSize: 26,
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: 0,
    // v1's muted grey, reachable now that the line is a text node in a card
    // rather than a `pill` with a forced colour.
    color: 'rgba(21,22,28,0.55)',
    strokeWidth: 0,
};
export const clientQuestion1 = {
    id: 'client-question-1',
    schemaVersion: 2,
    // WHAT THIS TEMPLATE NEEDS WRITTEN, in its own vocabulary. Four named fields
    // where the role enum offered `hook | body | cta` — and `asker`, which the
    // enum could not express at all, is one of them.
    content: {
        asker: {
            type: 'text',
            maxChars: 14,
            description: 'A fake-anonymised social handle for the person who asked — lowercase, ' +
                'no spaces, e.g. "sarah_x", "jess.m", "alex_r". Never a real full name, ' +
                'never a title. It renders after "Replying to " on the reply sticker.',
        },
        question: {
            type: 'text',
            maxChars: 110,
            description: 'A REAL QUESTION A CLIENT ASKED, quoted verbatim inside the reply ' +
                'bubble — not a line the business writes. FIRST PERSON and casual, ' +
                'exactly as it would arrive as a DM: it must contain "I", "my", "me" ' +
                'or "you" (e.g. "how long till I actually see results?", "does it ' +
                'hurt? I bruise really easily"). Lowercase or sentence case, question ' +
                'mark, ONE emoji at most. Ask about ONE specific, concrete worry about ' +
                'the focus service — pain, downtime, cost, how long it lasts, whether ' +
                'it suits them. NEVER a marketing hook, NEVER a pain-point pitch, ' +
                'NEVER third person: "Struggling with acne scars?" is exactly wrong.',
        },
        answers: {
            type: 'list',
            min: 2,
            max: 5,
            of: { type: 'text', maxChars: 80, description: 'One answer beat.' },
            description: 'The ANSWER to that question, broken into beats — one per b-roll clip, ' +
                'each alone in the lower third. The FIRST beat answers it DIRECTLY and ' +
                'plainly (yes / no / a number / a timeframe); later beats add the ' +
                'nuance or caveat. Each ≤10 words, sentence case, no trailing ' +
                'punctuation. Answer the question that was actually asked — do not ' +
                'pivot to a different benefit.',
        },
        cta: {
            type: 'text',
            maxChars: 60,
            optional: true,
            description: 'A short comment-prompt closer inviting the next question, ≤8 words. ' +
                'E.g. "Got a question? Drop it below", "Ask us anything". Not a hard ' +
                'booking push. Omit if the answers already land.',
        },
    },
    aspectRatios: ['portrait'],
    duration: { kind: 'fixed', frames: TOTAL_FRAMES },
    root: {
        kind: 'leaf',
        id: 'main',
        spine: [
            // Clips that visually answer the question. The bubble persists over all
            // of them; the scene cuts every 4 beats (v1 beatsPerEdit: 4).
            {
                kind: 'media-track',
                id: 'broll',
                duration: { kind: 'fill' },
                clips: {
                    source: 'query',
                    query: { kind: 'asset-clips', tag: 'procedure', count: [1, 5] },
                    required: true,
                },
                fit: 'contain-blur',
                cuts: { mode: 'beat-synced', beatsPerEdit: 4 },
                fillMode: 'loop',
            },
            // v1's top/bottom scrim, copied verbatim — dark at the bubble and at the
            // answer beats, near-clear through the middle of the frame.
            {
                kind: 'solid',
                id: 'scrim',
                duration: { kind: 'fill' },
                color: {
                    source: 'fixed',
                    value: 'linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.14) 34%, rgba(0,0,0,0.2) 62%, rgba(0,0,0,0.58) 100%)',
                },
            },
        ],
        overlays: [
            // The pinned reply sticker: ONE white speech-bubble card carrying the
            // "Replying to …" label above the question.
            //
            // This is a `group`, not a list: the CARD is the format. A vertical
            // `stack` owns the bubble chrome as its `container` Frame (background,
            // radius, padding, shadow, tail) and the two lines are its `text`
            // children, so they are inside one shape rather than being two
            // separately-chromed pills. The whole subtree carries a single
            // `entrance`, so the card pops once at frame 0 as one object and then
            // holds dead still for the rest of the clip — the signature of the
            // format.
            {
                kind: 'group',
                id: 'bubble',
                duration: { kind: 'fill' },
                // v1 bubble origin: top 8.5%, left 6%, width 62%.
                placement: { anchor: 'top-left', x: 0.06, y: 0.085, width: 0.62 },
                root: {
                    kind: 'stack',
                    direction: 'v',
                    // v1: the label carries marginBottom 8 above the question.
                    gap: 8,
                    // Flush-left inside the card (v1); the default is centre.
                    align: 'start',
                    container: {
                        background: 'rgba(255,255,255,0.97)',
                        radius: 22,
                        paddingX: 28,
                        paddingY: 22,
                        shadow: '0 10px 34px rgba(0,0,0,0.28)',
                        // The speech-bubble tail — the reason this reads as a reply
                        // sticker and not a caption card.
                        tail: 'bottom-left',
                    },
                    // v1: spring pop over 10 frames, applied to the WHOLE bubble.
                    entrance: 'pop',
                    entranceDurationFrames: 10,
                    children: [
                        // The reply label. "Replying to " is CHROME — authored, invariant,
                        // never generated — and the handle beside it is CONTENT. Splitting
                        // them is the whole distinction: one is a literal, the other a
                        // declared field, and neither has to pretend to be the other.
                        {
                            kind: 'stack',
                            direction: 'h',
                            width: 'hug',
                            align: 'center',
                            children: [
                                {
                                    kind: 'text',
                                    content: { source: 'literal', text: 'Replying to ' },
                                    style: 'caption',
                                    styleOverride: REPLY_LABEL_STYLE,
                                    align: 'left',
                                },
                                {
                                    kind: 'text',
                                    content: {
                                        source: 'slot',
                                        slot: {
                                            source: 'query',
                                            query: { kind: 'content', field: 'asker' },
                                            required: true,
                                        },
                                    },
                                    style: 'caption',
                                    styleOverride: REPLY_LABEL_STYLE,
                                    align: 'left',
                                },
                            ],
                        },
                        {
                            kind: 'text',
                            content: {
                                source: 'slot',
                                slot: {
                                    source: 'query',
                                    query: { kind: 'content', field: 'question' },
                                    required: true,
                                },
                            },
                            style: 'heading',
                            styleOverride: {
                                fontSize: 40,
                                fontWeight: 700,
                                textTransform: 'none',
                                // REEL_PALETTE.slate — near-black on the white card.
                                color: '#15161C',
                                // v1 question span. (The answer beats below run at 1.25, which
                                // is already the engine default, so they are left alone.)
                                lineHeight: 1.28,
                                strokeWidth: 0,
                            },
                            align: 'left',
                        },
                    ],
                },
            },
            // Answer beats in the lower third — exactly one on screen at a time, the
            // optional CTA last.
            {
                kind: 'staggered-list',
                id: 'answers',
                duration: { kind: 'fill' },
                items: {
                    texts: {
                        source: 'query',
                        query: { kind: 'content', field: 'answers' },
                        required: true,
                    },
                    style: 'heading',
                    styleOverride: {
                        fontSize: 50,
                        fontWeight: 800,
                        textTransform: 'none',
                        color: '#FFFFFF',
                        strokeWidth: 0,
                    },
                    container: 'plate',
                    // v1: fade + 18px rise on entry.
                    entrance: 'slide-up',
                },
                // v1 `ctaText` is optional — the layer renders the extra beat only when
                // the planner supplied one.
                trail: {
                    text: {
                        source: 'query',
                        query: { kind: 'content', field: 'cta' },
                        required: false,
                    },
                    style: 'heading',
                    styleOverride: {
                        fontSize: 40,
                        fontWeight: 800,
                        textTransform: 'none',
                        color: '#FFFFFF',
                        strokeWidth: 0,
                    },
                    container: 'button',
                    entrance: 'slide-up',
                },
                // `sequential` splits the master evenly across (answers + cta), exactly
                // like v1; `beatsPerItem` is the accumulate-mode knob and is unused
                // here, kept at the b-roll cut rate (4) as documentation of intent.
                stagger: { beatsPerItem: 4 },
                reveal: 'sequential',
                align: 'bottom',
                // v1: bottom padding 18%, horizontal padding 8% → width 84%.
                placement: { anchor: 'bottom', x: 0.5, y: 0.82, width: 0.84 },
            },
        ],
    },
    globals: {
        audio: {
            // Music only — no narration (v1 narrationMode: text_only,
            // supportsAiVoiceover: false).
            music: { source: 'query', query: { kind: 'music' }, required: false },
        },
        // No captions: the on-screen beats carry the message.
    },
};
//# sourceMappingURL=client-question-1.js.map